// Patterns API service for frontend
// Communicates with /api/patterns endpoints

import { API_BASE_URL } from '../config/settings';

export type PatternType = 'hook' | 'script' | 'visual' | 'cta' | 'concept';
export type PatternStatus = 'active' | 'emerging' | 'saturated' | 'declining';

export interface Pattern {
  id: string;
  type: PatternType;
  name: string;
  description?: string;
  examples?: string;
  status: PatternStatus;
  createdAt: number;
  validatedByHuman: boolean;
  videoCount: number;
  avgPerformance: number;
  similarityThreshold: number;
}

export interface PatternStats {
  total: number;
  byStatus: Record<PatternStatus, number>;
  byType: Record<PatternType, number>;
  topPerforming: Pattern[];
  emerging: Pattern[];
}

export interface PatternListQuery {
  type?: PatternType;
  status?: PatternStatus;
  minVideoCount?: number;
  minPerformance?: number;
  limit?: number;
  offset?: number;
}

export interface UserSelection {
  id: string;
  videoId?: string;
  patternId?: string;
  patternType?: PatternType;
  action: 'viewed' | 'used' | 'dismissed';
  createdAt: number;
  notes?: string;
}

class PatternsApi {
  private baseUrl = `${API_BASE_URL}/patterns`;

  /**
   * List patterns with optional filters
   */
  async listPatterns(query: PatternListQuery = {}): Promise<{ patterns: Pattern[]; total: number }> {
    const params = new URLSearchParams();
    if (query.type) params.set('type', query.type);
    if (query.status) params.set('status', query.status);
    if (query.minVideoCount) params.set('minVideoCount', String(query.minVideoCount));
    if (query.minPerformance) params.set('minPerformance', String(query.minPerformance));
    if (query.limit) params.set('limit', String(query.limit));
    if (query.offset) params.set('offset', String(query.offset));

    const url = `${this.baseUrl}?${params.toString()}`;
    const res = await fetch(url);
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to list patterns');
    }

    return json.data;
  }

  /**
   * Get pattern statistics
   */
  async getStats(): Promise<PatternStats> {
    const res = await fetch(`${this.baseUrl}/stats`);
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to get stats');
    }

    return json.data;
  }

  /**
   * Get emerging patterns
   */
  async getEmerging(type?: PatternType, limit = 20): Promise<{ patterns: Pattern[]; total: number }> {
    const params = new URLSearchParams();
    if (type) params.set('type', type);
    params.set('limit', String(limit));

    const res = await fetch(`${this.baseUrl}/emerging?${params.toString()}`);
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to get emerging patterns');
    }

    return json.data;
  }

  /**
   * Get saturated patterns (to avoid)
   */
  async getSaturated(type?: PatternType, limit = 20): Promise<{ patterns: Pattern[]; total: number }> {
    const params = new URLSearchParams();
    if (type) params.set('type', type);
    params.set('limit', String(limit));

    const res = await fetch(`${this.baseUrl}/saturated?${params.toString()}`);
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to get saturated patterns');
    }

    return json.data;
  }

  /**
   * Get patterns by type
   */
  async getByType(type: PatternType, status?: PatternStatus, limit = 50): Promise<{ patterns: Pattern[]; total: number }> {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    params.set('limit', String(limit));

    const res = await fetch(`${this.baseUrl}/type/${type}?${params.toString()}`);
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to get patterns by type');
    }

    return json.data;
  }

  /**
   * Get single pattern
   */
  async getPattern(type: PatternType, id: string): Promise<{ pattern: Pattern; videoCount: number; videoIds: string[] }> {
    const res = await fetch(`${this.baseUrl}/${type}/${id}`);
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Pattern not found');
    }

    return json.data;
  }

  /**
   * Create a new pattern
   */
  async createPattern(type: PatternType, data: {
    name: string;
    description?: string;
    examples?: string;
    status?: PatternStatus;
  }): Promise<Pattern> {
    const res = await fetch(`${this.baseUrl}/${type}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to create pattern');
    }

    return json.data;
  }

  /**
   * Update pattern
   */
  async updatePattern(type: PatternType, id: string, data: Partial<Pattern>): Promise<Pattern> {
    const res = await fetch(`${this.baseUrl}/${type}/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to update pattern');
    }

    return json.data;
  }

  /**
   * Delete pattern
   */
  async deletePattern(type: PatternType, id: string): Promise<void> {
    const res = await fetch(`${this.baseUrl}/${type}/${id}`, {
      method: 'DELETE'
    });
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to delete pattern');
    }
  }

  /**
   * Record user selection (when user uses/views/dismisses a pattern)
   */
  async recordSelection(data: {
    videoId?: string;
    patternId?: string;
    patternType?: PatternType;
    action: 'viewed' | 'used' | 'dismissed';
    notes?: string;
  }): Promise<UserSelection> {
    const res = await fetch(`${this.baseUrl}/selections`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to record selection');
    }

    return json.data;
  }

  /**
   * Get user selection history
   */
  async getSelections(action?: 'viewed' | 'used' | 'dismissed', limit = 50): Promise<{ selections: UserSelection[]; total: number }> {
    const params = new URLSearchParams();
    if (action) params.set('action', action);
    params.set('limit', String(limit));

    const res = await fetch(`${this.baseUrl}/selections?${params.toString()}`);
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to get selections');
    }

    return json.data;
  }

  /**
   * Analyze pattern trends with detailed reasoning
   */
  async analyzeTrends(): Promise<TrendAnalysisResult> {
    const res = await fetch(`${this.baseUrl}/analyze-trends`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
    const json = await res.json();

    if (!json.success) {
      throw new Error(json.error || 'Failed to analyze trends');
    }

    return json.data;
  }
}

export interface TrendChange {
  patternId: string;
  patternName: string;
  patternType: PatternType;
  previousStatus: PatternStatus;
  newStatus: PatternStatus;
  reasoning: string;
  metrics: {
    videoCount: number;
    avgPerformance: number;
    validated: boolean;
  };
}

export interface TrendAnalysisResult {
  analyzed: number;
  changes: TrendChange[];
  summary: string;
}

export const patternsApi = new PatternsApi();
